import { useEffect, useRef, useState } from 'react';
import { VERSION_PATH, isNewerBuild, parseBuildId, updateGuard } from '@utils/app-update';

const CHECK_INTERVAL_MS = 3 * 60 * 1000;
// A stale write ages out without emitting anything, so busy is also re-read on
// a timer rather than only when the guard changes.
const BUSY_RECHECK_MS = 5000;

/**
 * Keeps the update prompt back while `active`, for work that is not a write in
 * flight but would still be lost to a reload — an AI extraction being watched.
 */
export const useHoldAppUpdate = (active: boolean) => {
  useEffect(() => {
    if (!active) return;
    return updateGuard.hold();
  }, [active]);
};

/**
 * The key the extraction screen defers under, and the page arriving clears.
 *
 * A run finishing is not the moment to ask for a reload: the user is still on
 * the completion screen and has not yet reached the draft it produced, so a
 * reload drops them back where they started with nothing to show for the wait.
 * The screen that knows the run finished unmounts on the way to the draft, so
 * it hands the wait over by name rather than holding it itself.
 */
export const EXTRACTION_HANDOVER = 'extraction-handover';

/**
 * Long enough to cover a slow draft page, short enough that a user who goes
 * somewhere else entirely is not left on a stale build indefinitely.
 */
export const HANDOVER_TTL_MS = 2 * 60 * 1000;

/** Defers the prompt past the navigation that follows, while `active`. */
export const useDeferAppUpdateAcrossNavigation = (active: boolean) => {
  useEffect(() => {
    if (!active) return;
    updateGuard.defer(EXTRACTION_HANDOVER, HANDOVER_TTL_MS);
    // Refreshed on the way out, so the window measures the navigation rather
    // than however long the user left the finished screen sitting open first —
    // otherwise a run read at leisure hands over an already-expired deferral
    // and the prompt lands on the click that was meant to open the draft.
    return () => updateGuard.defer(EXTRACTION_HANDOVER, HANDOVER_TTL_MS);
  }, [active]);
};

/**
 * Ends the deferral once the user has actually landed somewhere else.
 *
 * Mounted inside the router, because the prompt's own modal is rendered
 * outside it and cannot watch the location. Keyed on the pathname rather than
 * on the draft route specifically: a finished run can land on the PIB draft,
 * the export editor or the PPFTZ page, and what the deferral is really waiting
 * for is the user leaving the extraction screen.
 */
export const useEndAppUpdateHandoverOnNavigate = (pathname: string) => {
  const arrivedAt = useRef(pathname);
  useEffect(() => {
    if (pathname === arrivedAt.current) return;
    arrivedAt.current = pathname;
    updateGuard.clearDeferral(EXTRACTION_HANDOVER);
  }, [pathname]);
};

/** True once a newer build is deployed and nothing in progress would be lost by reloading. */
export const useAppUpdatePrompt = (): boolean => {
  const [available, setAvailable] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    // Dev has no version.json and HMR keeps the tab current anyway.
    if (!import.meta.env.PROD || available) return;

    let cancelled = false;
    const check = async () => {
      try {
        const response = await fetch(`${VERSION_PATH}?t=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) return;
        const deployed = parseBuildId(await response.json());
        if (!cancelled && isNewerBuild(__APP_BUILD_ID__, deployed)) setAvailable(true);
      } catch {
        // Offline, or the SPA fallback answered with HTML: try again next time.
      }
    };
    const checkWhenVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };

    void check();
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', checkWhenVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', checkWhenVisible);
    };
  }, [available]);

  useEffect(() => {
    if (!available || shown) return;

    const showIfIdle = () => {
      if (!updateGuard.isBusy()) setShown(true);
    };
    showIfIdle();
    const unsubscribe = updateGuard.subscribe(showIfIdle);
    const timer = window.setInterval(showIfIdle, BUSY_RECHECK_MS);
    return () => {
      unsubscribe();
      window.clearInterval(timer);
    };
  }, [available, shown]);

  // Latched: once up, a background write starting behind the modal must not
  // make it flicker away.
  return shown;
};
